import React, { useCallback } from "react";
import * as XLSX from "xlsx";
import LoadingButton from "@material-ui/lab/LoadingButton";
import { Icon } from "@iconify/react";
import documentExport from "@iconify/icons-carbon/document-export";
import { tareasApi } from "src/services/tareasApi";
import { useSnackbar } from "src/components/snackbar";
import { formatFechaParaBackend } from "src/utils/formatTime";

export default function ExportToExcelTareas(props) {
  const {
    filename,
    sheetName,
    sucursalId,
    refEmpleado,
    rolObtenido,
    filtros,
    estadosParaQuery,
    sucursales = null,
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [triggerExport, { isLoading: isExporting }] =
    tareasApi.useLazyExportTareasAllQuery();

  const exportar = useCallback(async () => {
    try {
      const params = {
        id: sucursalId,
        refEmpleado,
        rolObtenido,
        busqueda: filtros.busqueda,
        estados: estadosParaQuery,
        tipoTarea: filtros.tipoTarea,
        fechaInicioDesde: filtros.fechaInicioDesde
          ? formatFechaParaBackend(filtros.fechaInicioDesde)
          : null,
        fechaInicioHasta: filtros.fechaInicioHasta
          ? formatFechaParaBackend(filtros.fechaInicioHasta)
          : null,
        fechaTerminoDesde: filtros.fechaTerminoDesde
          ? formatFechaParaBackend(filtros.fechaTerminoDesde)
          : null,
        fechaTerminoHasta: filtros.fechaTerminoHasta
          ? formatFechaParaBackend(filtros.fechaTerminoHasta)
          : null,
        cliente: filtros.cliente?.value || null,
        empleado: filtros.empleado?.value || null,
        sucursales,
      };

      const tareasCompletas = await triggerExport(params).unwrap();

      if (!tareasCompletas || tareasCompletas.length === 0) {
        enqueueSnackbar("No hay datos que exportar", {
          variant: "warning",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        return;
      }

      // Alerta si hay más de 10.000 registros
      if (tareasCompletas.length > 10000) {
        enqueueSnackbar(
          `Se exportarán ${tareasCompletas.length.toLocaleString()} tareas. Esto puede tardar más de lo habitual.`,
          {
            variant: "info",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            autoHideDuration: 5000,
          },
        );
      }

      const tareasParaExcel = tareasCompletas.map((tarea) => [
        tarea.refCliente,
        tarea.nombreCliente,
        tarea.nombre,
        tarea.tipoTarea,
        tarea.fechaInicio,
        tarea.fechaTermino,
        tarea.estado,
        tarea.valorTarea,
        tarea.tipoMonedaTarea,
        tarea.nombreEmpleado,
        tarea.apellidoEmpleado || "",
        tarea.diasTranscurridos,
        tarea.diasLimites || "",
      ]);

      // Crear Excel
      let worksheet = XLSX.utils.aoa_to_sheet([
        [
          "RUT Cliente",
          "Nombre Cliente",
          "Nombre Tarea",
          "Tipo Tarea",
          "Fecha Inicio",
          "Fecha Término",
          "Estado",
          "Valor Tarea",
          "Tipo Moneda",
          "Nombre Empleado",
          "Apellido Empleado",
          "Días Transcurridos",
          "Días Límite",
        ],
        ...tareasParaExcel,
      ]);

      let new_workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(new_workbook, worksheet, sheetName);
      XLSX.writeFile(new_workbook, `${filename}.xlsx`);

      enqueueSnackbar(
        `${tareasCompletas.length} tareas exportadas exitosamente`,
        {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        },
      );
    } catch (error) {
      console.error("Error al exportar tareas:", error);
      enqueueSnackbar(error?.data?.message || "Error al exportar tareas", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  }, [
    enqueueSnackbar,
    estadosParaQuery,
    filename,
    filtros.busqueda,
    filtros.cliente?.value,
    filtros.empleado?.value,
    filtros.fechaInicioDesde,
    filtros.fechaInicioHasta,
    filtros.fechaTerminoDesde,
    filtros.fechaTerminoHasta,
    filtros.tipoTarea,
    refEmpleado,
    rolObtenido,
    sheetName,
    sucursalId,
    sucursales,
    triggerExport,
  ]);

  return (
    <LoadingButton
      variant="contained"
      onClick={exportar}
      startIcon={<Icon icon={documentExport} />}
      style={{ minWidth: "170px" }}
      loading={isExporting}
    >
      Exportar tareas
    </LoadingButton>
  );
}
